import { createApiClient, getRuntimeConfig, useAuth, type ApiClient } from '@berky/auth'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import type {
  FavoritesResponse,
  ModuleNavigationSetting,
  NavigationCategory,
} from './types'

const moduleNavigationKey = ['platform-navigation', 'modules'] as const
const navigationCategoriesKey = ['platform-navigation', 'categories'] as const
const favoritesKey = ['platform-navigation', 'favorites'] as const

function useNavigationApi(): ApiClient {
  const { getAccessToken, reportApiAuthError } = useAuth()
  const { platformScope } = getRuntimeConfig()
  const scopes = useMemo(() => (platformScope ? [platformScope] : []), [platformScope])

  return useMemo(
    () =>
      createApiClient({
        baseUrl: '/api/platform/navigation',
        scopes,
        getAccessToken,
        onAuthError: reportApiAuthError,
      }),
    [scopes, getAccessToken, reportApiAuthError],
  )
}

export function useModuleNavigation() {
  const { user, isLoading } = useAuth()
  const api = useNavigationApi()
  return useQuery({
    queryKey: moduleNavigationKey,
    enabled: Boolean(user) && !isLoading,
    queryFn: ({ signal }) => api.get<ModuleNavigationSetting[]>('/modules', { signal }),
  })
}

export function useNavigationCategories() {
  const { user, isLoading } = useAuth()
  const api = useNavigationApi()
  return useQuery({
    queryKey: navigationCategoriesKey,
    enabled: Boolean(user) && !isLoading,
    queryFn: ({ signal }) => api.get<NavigationCategory[]>('/categories', { signal }),
  })
}

export function useSaveModuleNavigation() {
  const api = useNavigationApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (settings: ModuleNavigationSetting[]) =>
      api.put<void>('/modules', { modules: settings }),
    onSuccess: (_response, settings) => {
      queryClient.setQueryData(moduleNavigationKey, settings)
    },
  })
}

export function useCreateNavigationCategory() {
  const api = useNavigationApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (
      category: Pick<
        NavigationCategory,
        'title' | 'description' | 'markerText' | 'colorHex'
      >,
    ) => api.post<NavigationCategory>('/categories', category),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: navigationCategoriesKey })
    },
  })
}

export function useSaveNavigationCategories() {
  const api = useNavigationApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (categories: NavigationCategory[]) =>
      api.put<void>('/categories', { categories }),
    onSuccess: (_response, categories) => {
      queryClient.setQueryData(navigationCategoriesKey, categories)
    },
  })
}

export function useFavorites() {
  const { user, isLoading } = useAuth()
  const api = useNavigationApi()
  return useQuery({
    queryKey: [...favoritesKey, user?.id],
    enabled: Boolean(user) && !isLoading,
    queryFn: ({ signal }) => api.get<FavoritesResponse>('/favorites', { signal }),
  })
}

export function useSaveFavorites() {
  const { user } = useAuth()
  const api = useNavigationApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (moduleIds: string[]) => api.put<void>('/favorites', { moduleIds }),
    onSuccess: (_response, moduleIds) => {
      queryClient.setQueryData<FavoritesResponse>([...favoritesKey, user?.id], {
        moduleIds,
      })
    },
  })
}
